// src/pages/DebtStudio/components/Issues/DealStructure/hooks/optimizeDenominations.js

import dayjs from "lib/dayjs";
import { dayCountFraction } from "lib/dayCount";
import {
  buildDebtServiceSchedule,
  normalizeTermCoupons,
} from "pages/DebtStudio/lib/buildDebtServiceSchedule";

/**
 * Optimize denomination-level allocation after LDS solve.
 *
 * Design:
 * - Single-pass repair optimizer
 * - Tests only later-to-earlier par-neutral swaps:
 *      later maturity:   -denomination
 *      earlier maturity: +denomination
 * - Accepts at most one swap, then stops
 *
 * Objective:
 * - Reduce total interest, using sum(row.interest)
 *
 * Performance design:
 * - Pre-screen candidate pairs using estimated marginal interest improvement
 * - Only rebuild full debt service for candidates that can improve interest
 *
 * Constraints:
 * - No negative principal
 * - Denomination grid preserved
 * - For net_proceeds, netProceeds must remain >= proceedsTarget
 * - Approximate LDS levelness must satisfy a bond-year based dispersion cap
 * - First short bond year tolerance is scaled by the first-year stub fraction
 */
export function optimizeDenominations({
  result,
  denomination,
  targetType,
  proceedsTarget,
  form,
  costOfIssuance,
  computeCapIUse,
  roundPrice3,
  roundCents,
  dsShapeTolerance,
}) {
  if (!result?.scheduleForMath?.length) return result;

  const denom = Number(denomination) || 0;
  if (!(denom > 0)) return result;

  const dispersionTolerance = Number(dsShapeTolerance ?? denom * 0.6); // base tolerance
  const minImprovement = 0.01;
  const baseSchedule = result.scheduleForMath.map((r) => ({ ...r }));
  const normalizedForEstimate = normalizeTermCoupons(baseSchedule);
  const dc = form?.dayCount || form?.day_count || "30/360";
  const interestStartDate = form?.datedDate || form?.dated_date || null;

  function isoDate(value) {
    const d = value ? dayjs(value) : null;
    if (!d || !d.isValid()) return "";
    return d.format("YYYY-MM-DD");
  }

  function dateValue(row) {
    const d = row?.date ? dayjs(row.date) : null;
    return d && d.isValid() ? d.valueOf() : 0;
  }

  function isPrincipalMaturity(row) {
    return (Number(row?.principal) || 0) > 0;
  }

  function parseCouponAnnual(v) {
    if (v == null || v === "") return 0;

    let n = Number(v);
    if (!Number.isFinite(n)) return 0;
    if (n > 1) n = n / 100;
    if (n < 0) return 0;

    return n;
  }

  function estimatedInterestForDenom(row) {
    const start = interestStartDate ? dayjs(interestStartDate) : null;
    const end = row?.date ? dayjs(row.date) : null;

    if (!start || !end || !start.isValid() || !end.isValid()) return 0;
    if (!end.isAfter(start, "day")) return 0;

    const couponAnnual = parseCouponAnnual(row?.coupon);
    if (!(couponAnnual > 0)) return 0;

    const frac = dayCountFraction(start, end, dc);
    if (!Number.isFinite(frac) || frac <= 0) return 0;

    return denom * couponAnnual * frac;
  }

  function estimateInterestImprovement(fromIdx, toIdx) {
    const fromRow = normalizedForEstimate[fromIdx] || baseSchedule[fromIdx];
    const toRow = normalizedForEstimate[toIdx] || baseSchedule[toIdx];

    const removedInterest = estimatedInterestForDenom(fromRow);
    const addedInterest = estimatedInterestForDenom(toRow);

    return removedInterest - addedInterest;
  }

  function computeFirstYearFactor(scheduleRows) {
    const datedDate = form?.datedDate || form?.dated_date || null;
    const deliveryDate = form?.deliveryDate || form?.delivery_date || null;
    const dayCount = form?.dayCount || form?.day_count || "30/360";

    const startDate = deliveryDate || datedDate;

    const principalDates = scheduleRows
      .filter((r) => (Number(r?.principal) || 0) > 0)
      .map((r) => isoDate(r?.date))
      .filter(Boolean)
      .sort();

    const firstPrincipalDate = principalDates[0];

    if (!startDate || !firstPrincipalDate) return 1;

    const start = dayjs(startDate).startOf("day");
    const end = dayjs(firstPrincipalDate).startOf("day");

    if (!start.isValid() || !end.isValid()) return 1;
    if (!end.isAfter(start, "day")) return 1;

    const frac = dayCountFraction(start, end, dayCount);

    if (!Number.isFinite(frac) || frac <= 0) return 1;

    return frac > 0 && frac < 1 ? frac : 1;
  }

  function computeBondYearDS(scheduleRows, dsRows) {
    const principalDates = new Set(
      scheduleRows
        .filter((r) => (Number(r?.principal) || 0) > 0)
        .map((r) => isoDate(r?.date))
        .filter(Boolean)
    );

    const bondYearDS = [];
    let current = 0;

    for (const row of dsRows || []) {
      const d = isoDate(row?.date);
      if (!d) continue;

      current += Number(row?.netDebtService ?? row?.total ?? 0) || 0;

      if (principalDates.has(d)) {
        bondYearDS.push(roundCents(current));
        current = 0;
      }
    }

    if (Math.abs(current) > 0.005) {
      bondYearDS.push(roundCents(current));
    }

    return bondYearDS;
  }

  function computeBondYearStats(scheduleRows, dsRows) {
    const bondYearDS = computeBondYearDS(scheduleRows, dsRows);
    const firstYearFactor = computeFirstYearFactor(scheduleRows);

    if (!bondYearDS.length) {
      return {
        bondYearDS: [],
        aads: 0,
        firstYearFactor,
        maxDeviation: 0,
        firstDeviation: 0,
        lastDeviation: 0,
        maxInteriorDeviation: 0,
        passes: true,
      };
    }

    const weightedYearCount =
      bondYearDS.length > 1
        ? firstYearFactor + (bondYearDS.length - 1)
        : firstYearFactor || 1;

    const totalDS = bondYearDS.reduce((s, x) => s + (Number(x) || 0), 0);
    const aads = weightedYearCount > 0 ? totalDS / weightedYearCount : totalDS;

    let maxDeviation = 0;
    let firstDeviation = 0;
    let lastDeviation = 0;
    let maxInteriorDeviation = 0;

    for (let i = 0; i < bondYearDS.length; i++) {
      const factor = i === 0 ? firstYearFactor : 1;
      const target = aads * factor;
      const tolerance = dispersionTolerance * factor;
      const deviation = Math.abs((Number(bondYearDS[i]) || 0) - target);

      if (i === 0) firstDeviation = deviation;
      if (i === bondYearDS.length - 1) lastDeviation = deviation;
      if (i > 0 && i < bondYearDS.length - 1) {
        maxInteriorDeviation = Math.max(maxInteriorDeviation, deviation);
      }

      maxDeviation = Math.max(maxDeviation, deviation);

      if (deviation > tolerance + 0.005) {
        return {
          bondYearDS,
          aads,
          firstYearFactor,
          maxDeviation,
          firstDeviation,
          lastDeviation,
          maxInteriorDeviation,
          passes: false,
          failedIndex: i,
          failedDeviation: deviation,
          failedTolerance: tolerance,
        };
      }
    }

    return {
      bondYearDS,
      aads,
      firstYearFactor,
      maxDeviation,
      firstDeviation,
      lastDeviation,
      maxInteriorDeviation,
      passes: true,
    };
  }

  function computeMetrics(scheduleRows) {
    const ds = buildDebtServiceSchedule({
      rows: scheduleRows,
      form,
    });

    const totalInterest = ds.reduce(
      (s, r) => s + (Number(r.interest) || 0),
      0
    );

    const par = scheduleRows.reduce(
      (s, r) => s + (Number(r.principal) || 0),
      0
    );

    const gross = scheduleRows.reduce((acc, r) => {
      const p3 = roundPrice3(r.price ?? 100);
      return acc + roundCents((Number(r.principal) || 0) * (p3 / 100));
    }, 0);

    const coi =
      typeof costOfIssuance === "function"
        ? costOfIssuance(par)
        : Number(costOfIssuance) || 0;

    const capiUse = computeCapIUse(scheduleRows, ds);
    const net = roundCents(gross - coi - capiUse);
    const bondYearStats = computeBondYearStats(scheduleRows, ds);

    return {
      ds,
      bondYearStats,
      totalInterest,
      par,
      gross,
      net,
      coi,
      capiUse,
    };
  }

  function isProceedsFeasible(metrics) {
    if (targetType !== "net_proceeds") return true;
    return metrics.net + 0.005 >= proceedsTarget;
  }

  function isCandidateFeasible(metrics) {
    return isProceedsFeasible(metrics) && !!metrics?.bondYearStats?.passes;
  }

  function buildCandidate(fromIdx, toIdx) {
    return baseSchedule.map((r, idx) => {
      if (idx === fromIdx) {
        return { ...r, principal: (Number(r.principal) || 0) - denom };
      }

      if (idx === toIdx) {
        return { ...r, principal: (Number(r.principal) || 0) + denom };
      }

      return { ...r };
    });
  }

  const baselineMetrics = computeMetrics(baseSchedule);

  function buildCandidatePairs() {
    const pairs = [];

    for (let fromIdx = 0; fromIdx < baseSchedule.length; fromIdx++) {
      const fromRow = baseSchedule[fromIdx];

      if (!isPrincipalMaturity(fromRow)) continue;
      if ((Number(fromRow.principal) || 0) < denom) continue;

      const fromDateValue = dateValue(fromRow);
      if (!fromDateValue) continue;

      for (let toIdx = 0; toIdx < baseSchedule.length; toIdx++) {
        if (fromIdx === toIdx) continue;

        const toRow = baseSchedule[toIdx];
        const toDateValue = dateValue(toRow);
        if (!toDateValue) continue;

        if (fromDateValue <= toDateValue) continue;

        const estimatedImprovement = estimateInterestImprovement(fromIdx, toIdx);

        if (estimatedImprovement <= minImprovement) continue;

        pairs.push({
          fromIdx,
          toIdx,
          fromDate: fromRow.date,
          toDate: toRow.date,
          estimatedImprovement,
        });
      }
    }

    pairs.sort((a, b) => b.estimatedImprovement - a.estimatedImprovement);

    return pairs;
  }

  function findBestLaterToEarlierSwap() {
    const pairs = buildCandidatePairs();
    let best = null;
    let validatedCount = 0;

    for (const pair of pairs) {
      const candidate = buildCandidate(pair.fromIdx, pair.toIdx);
      const metrics = computeMetrics(candidate);
      validatedCount += 1;

      if (!isCandidateFeasible(metrics)) continue;

      const improvement =
        baselineMetrics.totalInterest - metrics.totalInterest;

      if (improvement <= minImprovement) continue;

      const better =
        !best ||
        improvement > best.improvement + minImprovement ||
        (Math.abs(improvement - best.improvement) <= minImprovement &&
          metrics.bondYearStats.maxDeviation <
            best.metrics.bondYearStats.maxDeviation - 0.005);

      if (better) {
        best = {
          schedule: candidate,
          metrics,
          improvement,
          estimatedImprovement: pair.estimatedImprovement,
          fromIdx: pair.fromIdx,
          toIdx: pair.toIdx,
          fromDate: pair.fromDate,
          toDate: pair.toDate,
          screenedCount: pairs.length,
          validatedCount,
        };
      }
    }

    if (best) {
      return {
        ...best,
        screenedCount: pairs.length,
        validatedCount,
      };
    }

    return {
      none: true,
      screenedCount: pairs.length,
      validatedCount,
    };
  }

  const bestSwap = findBestLaterToEarlierSwap();

  let optSchedule = baseSchedule;
  let metrics = baselineMetrics;

  if (bestSwap && !bestSwap.none) {
    optSchedule = bestSwap.schedule;
    metrics = bestSwap.metrics;
  }

  let finalSchedule = optSchedule;

  const lastByGroup = {};
  for (const r of finalSchedule) {
    if (r?.bond_type === "term" && r?.term_group) {
      const g = r.term_group;
      if (!lastByGroup[g] || (r.date && r.date > lastByGroup[g].date)) {
        lastByGroup[g] = r;
      }
    }
  }

  finalSchedule = finalSchedule.map((r) => {
    if (r?.bond_type === "term" && r?.term_group) {
      const isLast = lastByGroup[r.term_group] === r;
      return isLast ? { ...r } : { ...r, yield: null, coupon: null };
    }

    return { ...r };
  });

  return {
    ...result,
    schedule: finalSchedule,
    scheduleForMath: optSchedule,
    parAmount: metrics.par,
    grossProceeds: metrics.gross,
    netProceeds: metrics.net,
    coi: metrics.coi,
    capiUse: metrics.capiUse,
  };
}